// Briques communes de la fiche parcelle : carte de rubrique, ligne « libellé / valeur / source », formats.
import { AlertTriangle } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";

export const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const COL = {
  bg: "#F9F8F6", green: "#2D6A4F", greenDark: "#1B4332", greenSoft: "#E8F1EC", text: "#1A1A1A", muted: "#4A4A4A",
  amber: "#B45309", amberSoft: "#FEF3C7", line: "rgba(26,26,26,0.1)",
};

export const SOURCES = {
  saisie: "Saisie", rpg: "RPG (IGN)", cadastre: "Cadastre (IGN)", ign: "IGN", bd_topo: "BD TOPO",
  open_meteo: "Open-Meteo", calcul: "Calculé", convention: "Convention",
};

export const nombre = (v, max = 2) => (typeof v === "number" ? v.toLocaleString("fr-FR", { maximumFractionDigits: max }) : v);

export const dateFr = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return d ? `${d}/${m}/${y}` : iso;
};

export const dateHeureFr = (iso) => {
  if (!iso) return "";
  const t = String(iso).slice(11, 16);
  return `${dateFr(iso)}${t ? ` à ${t.replace(":", " h ")}` : ""}`;
};

export const isErreur = (r) => r && !Array.isArray(r) && r.erreur;

// Valeur affichée {valeur, unite?, source, date?} : la valeur, puis sa source en petit gris ; absente = « À renseigner ».
export function Valeur({ v, format, testId, align = "right" }) {
  if (!v) {
    return <span className="text-sm font-medium" style={{ color: COL.amber }} data-testid={testId}>À renseigner</span>;
  }
  const txt = format ? format(v.valeur) : nombre(v.valeur);
  return (
    <span className={`flex flex-col min-w-0 ${align === "left" ? "items-start text-left" : "items-end text-right"}`} data-testid={testId}>
      <span className="text-sm font-medium break-words" style={{ color: COL.text }}>
        {txt}{v.unite ? ` ${v.unite}` : ""}
      </span>
      <span className="text-[11px] text-gray-500">
        {SOURCES[v.source] || v.source}{v.date ? ` · ${dateFr(v.date)}` : ""}
      </span>
    </span>
  );
}

export function Ligne({ label, v, format, children, testId }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0" style={{ borderColor: COL.line }}>
      <span className="text-sm shrink-0" style={{ color: COL.muted }}>{label}</span>
      {children !== undefined ? <span className="text-sm text-right min-w-0" style={{ color: COL.text }}>{children}</span>
        : <Valeur v={v} format={format} testId={testId} />}
    </div>
  );
}

export function Carte({ titre, numero, icon: Icon, children, className = "", testId, action }) {
  return (
    <section className={`bg-white rounded-2xl border p-4 ${className}`} style={{ borderColor: COL.line }} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="text-base font-bold flex items-center gap-2" style={{ color: COL.text, fontFamily: "Manrope, sans-serif" }}>
          {Icon && <Icon className="w-4 h-4" style={{ color: COL.green }} />}
          {numero ? <span className="text-xs text-gray-400 font-semibold">{numero}</span> : null}
          {titre}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Indisponible() {
  return (
    <p className="text-sm flex items-center gap-2 py-2" style={{ color: COL.amber }}>
      <AlertTriangle className="w-4 h-4" /> Rubrique indisponible pour le moment.
    </p>
  );
}

export function Vide({ children }) {
  return <p className="text-sm text-gray-500 py-2">{children}</p>;
}

// Téléchargement d'un fichier de l'API (jeton dans l'en-tête), sous le nom donné.
export async function telecharger(url, filename, options = {}) {
  try {
    const res = await authFetch(url, options);
    if (!res.ok) throw new Error();
    const blob = await res.blob();
    const u = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(u);
    toast.success("Fichier téléchargé");
    return true;
  } catch {
    toast.error("Téléchargement impossible. Vérifiez la connexion.");
    return false;
  }
}
